"use client";

import { useId, useMemo, useState } from "react";
import { ArrowRight, MapPin, RotateCcw, Search } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
interface Community {
  name: string;
  description: string;
}
const BALTIMORE_COUNTY_COMMUNITIES: Community[] = [{
  name: "Towson",
  description: "County seat along the I-695 Beltway"
}, {
  name: "Catonsville",
  description: "Historic community southwest of Baltimore along Route 40"
}, {
  name: "Owings Mills",
  description: "Commercial and residential corridor along I-795"
}, {
  name: "Dundalk",
  description: "Southeast waterfront community near the Patapsco River"
}, {
  name: "Parkville",
  description: "Northeast residential hub along Harford Road"
}, {
  name: "Essex",
  description: "Eastern coastal community along Back River and Middle River"
}, {
  name: "Pikesville",
  description: "Northwest corridor along Reisterstown Road"
}, {
  name: "Cockeysville",
  description: "Central industrial and residential center along the I-83 corridor"
}, {
  name: "Reisterstown",
  description: "Historic northwest terminus community on MD 140"
}];
export default function BaltimoreCountyCommunities() {
  const searchInputId = useId();
  const [searchTerm, setSearchTerm] = useState("");
  const filteredCommunities = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    if (!query) return BALTIMORE_COUNTY_COMMUNITIES;
    return BALTIMORE_COUNTY_COMMUNITIES.filter(community => community.name.toLowerCase().includes(query));
  }, [searchTerm]);
  const handleClear = () => {
    setSearchTerm("");
  };
  return <section id="baltimorecountycommunities" data-nav="light" className="bg-background text-foreground py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Block */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <Badge variant="outline" className="border-border text-foreground font-semibold px-3 py-1">
            LOCAL SERVICE COVERAGE
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-foreground">
            Cities &amp; Communities We Serve in Baltimore County
          </h2>
          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
            Local rapid-response mitigation teams dispatched directly across Baltimore County municipalities.
          </p>
        </div>

        {/* Live Search Bar */}
        <div className="mt-10 sm:mt-12 max-w-md mx-auto">
          <div className="relative">
            <label htmlFor={searchInputId} className="sr-only">
              Search your city
            </label>
            <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-muted-foreground">
              <Search className="size-4" aria-hidden="true" />
            </div>
            <Input id={searchInputId} type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search your city..." className="pl-10 pr-10 h-11 bg-card text-card-foreground border-border rounded-lg shadow-xs focus-visible:ring-primary" />
            {searchTerm && <div className="absolute inset-y-0 right-0 pr-1.5 flex items-center">
                <Button type="button" variant="ghost" size="sm" onClick={handleClear} className="h-8 w-8 p-0 text-muted-foreground hover:text-foreground" aria-label="Clear search">
                  <RotateCcw className="size-3.5" aria-hidden="true" />
                </Button>
              </div>}
          </div>
          <div className="mt-2 text-xs text-muted-foreground text-center">
            Showing {filteredCommunities.length} of {BALTIMORE_COUNTY_COMMUNITIES.length} locations
          </div>
        </div>

        {/* Communities Grid */}
        <div className="mt-10 sm:mt-12">
          {filteredCommunities.length > 0 ? <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4 sm:gap-6">
              {filteredCommunities.map((community, i) => <Card key={community.name} data-index={i} className="bg-card text-card-foreground border border-border rounded-lg shadow-xs hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 flex flex-col justify-between">
                  <CardHeader className="p-5 pb-3">
                    <div className="flex items-start gap-2.5">
                      <div className="p-1.5 rounded-md bg-muted text-primary shrink-0 mt-0.5">
                        <MapPin className="size-4" aria-hidden="true" />
                      </div>
                      <CardTitle className="text-lg font-semibold tracking-tight text-foreground leading-snug">
                        {community.name}
                      </CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="p-5 pt-0">
                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {community.description}
                    </p>
                  </CardContent>
                </Card>)}
            </div> : <div className="text-center py-16 px-4 bg-muted/40 rounded-xl border border-dashed border-border max-w-xl mx-auto">
              <MapPin className="size-8 mx-auto text-muted-foreground/60 mb-3" aria-hidden="true" />
              <h3 className="text-base font-semibold text-foreground">No community found</h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
                No local areas match &ldquo;{searchTerm}&rdquo;. We serve all of Baltimore County.
              </p>
              <div className="mt-4">
                <Button variant="outline" size="sm" onClick={handleClear} className="border-border">
                  Clear search filter
                </Button>
              </div>
            </div>}
        </div>

        {/* Emergency CTA */}
        <div className="mt-16 sm:mt-20 pt-10 border-t border-border text-center">
          <div className="max-w-xl mx-auto space-y-4">
            <p className="text-base sm:text-lg font-medium text-foreground">
              Need emergency restoration services?
            </p>
            <div>
              <Button asChild size="lg" className="rounded-lg shadow-sm font-medium">
                <Link to="/ContactRequestServicePage" className="inline-flex items-center gap-2">
                  Request Emergency Service
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}